import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Box, Button, Chip, CircularProgress, IconButton, List, ListItem,
  Stack, Tooltip, Typography
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import FileDropField from '../../../shared/components/FileDropField';
import { RADIUS, SHADOWS } from '../../../theme/muiTheme';
import { formatSize } from '../../../shared/utils/number';
import { HistoryButton, SourceIcon } from './DataSourceCard';

function SnapshotFileRow({ item, onRemove, disabled }) {
  const { t } = useTranslation();

  return (
    <ListItem
      disableGutters
      data-testid={`snapshot-file-${item.key}`}
      data-name={item.name}
      sx={(theme) => ({
        px: 1,
        py: 0.5,
        borderRadius: 1,
        mb: 0.25,
        '&:hover': {
          bgcolor: alpha(
            theme.palette.text.primary,
            theme.palette.mode === 'dark' ? 0.06 : 0.03,
          ),
        },
      })}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.25}
        sx={{ flex: 1, minWidth: 0 }}
      >
        {/* Tag status */}
        <Chip
          size="small"
          variant="outlined"
          label={t(
            item.isRetained
              ? 'dataInput.scope3Af.retainedFile'
              : 'dataInput.scope3Af.localFile',
          )}
          sx={(theme) => ({
            height: 22,
            '& .MuiChip-label': {
              px: 0.9,
              fontSize: '0.7rem',
              fontWeight: 500,
            },
            color: item.isRetained ? theme.palette.success.main : theme.palette.grey[700],
            borderColor: item.isRetained ? theme.palette.success.main : theme.palette.grey[400],
            bgcolor: alpha(
              item.isRetained ? theme.palette.success.main : theme.palette.grey[500],
              theme.palette.mode === 'dark' ? 0.06 : 0.04,
            ),
            flexShrink: 0,
          })}
        />

        {/* Icon delete */}
        <Tooltip title={t('dataInput.scope3Af.deleteFile')}>
          <span>
            <IconButton
              edge="start"
              size="small"
              data-testid={`snapshot-delete-${item.key}`}
              aria-label={t('dataInput.scope3Af.deleteFile')}
              onClick={() => onRemove(item.key)}
              disabled={disabled}
              sx={(theme) => ({
                ml: 0.5,
                p: 0.25,
                color: theme.palette.text.secondary,
                flexShrink: 0,
                '&:hover': {
                  color: theme.palette.error.main,
                  bgcolor: alpha(theme.palette.error.main, 0.06),
                },
              })}
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Tên file + (optional) size */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={item.name}
          >
            {item.name}
          </Typography>
          {item.size != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.15 }}
            >
              {formatSize(item.size)}
            </Typography>
          )}
        </Box>
      </Stack>
    </ListItem>
  );
}

export default function Scope3AfSnapshotCard({
  title,
  description,
  icon,
  tone,
  files = [],
  loading = false,
  busy = false,
  disabled = false,
  canUpload = true,
  hasChanges = false,
  onFileChange,
  onRemoveFile,
  onUpload,
  onViewProgress,
  onViewResult,
  viewResultDisabled = false,
  onTemplateDownload,
  templateDownloading = false,
  templateDownloadDisabled = false,
  onHistory,
  fileError,
}) {
  const { t } = useTranslation();

  return (
    <Box
      sx={(theme) => ({
        bgcolor: 'background.paper',
        border: 1,
        borderColor: alpha(theme.palette.text.primary, 0.12),
        borderRadius: `${RADIUS.control}px`,
        boxShadow: theme.palette.mode === 'dark' ? 'none' : SHADOWS.e1,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <SourceIcon icon={icon} tone={tone} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 750 }}>{title}</Typography>
              {description && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                  {description}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ alignSelf: { xs: 'flex-end', sm: 'center' }, flexShrink: 0 }}
          >
            {(canUpload || busy) && (
              <Tooltip
                title={!busy && !hasChanges ? t('dataInput.scope3Af.noChanges') : ''}
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={busy
                      ? <CircularProgress size={14} role="presentation" aria-hidden="true" />
                      : <UploadFileRoundedIcon />}
                    onClick={busy ? onViewProgress : onUpload}
                    disabled={busy
                      ? !onViewProgress
                      : (disabled || files.length === 0 || !hasChanges)}
                  >
                    {busy ? t('dataInput.actions.processing') : t('dataInput.actions.upload')}
                  </Button>
                </span>
              </Tooltip>
            )}
            {onViewResult && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRoundedIcon />}
                onClick={onViewResult}
                disabled={viewResultDisabled || busy}
              >
                {t('dataInput.actions.viewResult')}
              </Button>
            )}
            <HistoryButton onClick={onHistory} />
          </Stack>
        </Stack>
      </Box>
      {canUpload && (
        <Box
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.025 : 0.012),
            borderTop: 1,
            borderColor: 'divider',
            p: { xs: 1.5, sm: 2 },
          })}
        >
          {onTemplateDownload && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="caption" color="text.secondary">
                {t('dataInput.template.helper')}
              </Typography>
              <Button
                variant="text"
                size="small"
                startIcon={templateDownloading
                  ? <CircularProgress size={14} role="presentation" aria-hidden="true" />
                  : <DownloadRoundedIcon />}
                onClick={onTemplateDownload}
                disabled={templateDownloading || templateDownloadDisabled}
                sx={{ flexShrink: 0 }}
              >
                {t(templateDownloading
                  ? 'dataInput.template.downloading'
                  : 'dataInput.template.download')}
              </Button>
            </Stack>
          )}

          {files.length > 0 && (
            <List dense disablePadding sx={{ mb: 1.25 }}>
              {files.map((item) => (
                <SnapshotFileRow
                  key={item.key}
                  item={item}
                  onRemove={onRemoveFile}
                  disabled={disabled || busy}
                />
              ))}
            </List>
          )}

          <Box
            sx={{
              pointerEvents: disabled ? 'none' : 'auto',
              opacity: disabled ? 0.58 : 1,
            }}
          >
            <FileDropField
              multiple
              accept={'.xlsx'}
              maxSizeMb={25}
              acceptLabel={t('dataInput.file.accepted', { max: 25 })}
              onChange={onFileChange}
              externalError={fileError}
            />
          </Box>
          {loading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <CircularProgress size={14} role="presentation" aria-hidden="true" />
              <Typography variant="caption" color="text.secondary">
                {t('dataInput.scope3Af.loading')}
              </Typography>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
}